import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router";
import { BadgeCheck, Download, Lock, Pencil, Unlock } from "lucide-react";
import { useApi, usePermission } from "@ncp/api-client";
import { formatDate, formatNumber, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Field, InfoHint, Input, NativeSelect, PageHeader, Popover, PopoverContent, PopoverTrigger, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger, Textarea, cn, notifySaveFailed, notifySaved, useSave } from "@ncp/ui";
import { useStaffName } from "@/lib/staff-names";
import { OperationsPanel } from "./OperationsPanel";
import { FigureFilesSheet } from "./FigureFiles";
import { questionOf, tableKey, useCompStats, useSaveReturn, useSignOffs, type CellSource, type DecisionListRow, type ItemListRow, type SignOff, type StatCell, type StatColumn, type StatTable } from "./api";

const SECTIONS = ["operations", "antitrust", "mergers", "powers", "sanctions", "advocacy", "judicial", "institution"] as const;
type Section = (typeof SECTIONS)[number];

/** Which decision lists the questionnaire asks for on each tab. */
const LISTS: Partial<Record<Section, string[]>> = { antitrust: ["3.10", "4.9", "5.9", "6.10"], mergers: ["7.6"], sanctions: ["10.8", "10.10"] };
/** Which lists of studies and opinions it asks for. */
const ITEM_LISTS: Partial<Record<Section, string[]>> = { advocacy: ["13.2", "13.3"] };
/** The five years the OECD questionnaire covers. */
const QUESTIONNAIRE_PERIOD = { from: 2020, to: 2024 };

const SOURCE_STYLE: Record<CellSource, string> = {
  Computed: "bg-seablue-500",
  Entered: "bg-aegold-600",
  Derived: "bg-sand-500",
  Missing: "border border-dashed border-sand-400 bg-transparent",
};

/**
 * Statistics: the Department's operational figures, and the tables of the OECD peer-review questionnaire by question number. Each figure
 * shows whether the platform computed it from the files or the Department entered it; authorised staff enter or correct figures in place
 * and export the whole set in the questionnaire's layout.
 */
export function StatisticsPage() {
  const { t } = useTranslation();
  const api = useApi();
  const { can } = usePermission();
  const canEdit = can("statistics.edit");
  const [params, setParams] = useSearchParams();
  const section: Section = SECTIONS.includes(params.get("tab") as Section) ? (params.get("tab") as Section) : "operations";
  const lastFullYear = new Date().getFullYear() - 1;
  const to = Number(params.get("to")) || lastFullYear;
  const from = Number(params.get("from")) || to - 4;
  const report = useCompStats(from, to);
  // Phase I / Phase II: the reading of the two reviews where the legal rules register holds it, awaiting the Department's approval until recorded (2.13).
  const phasesNote = t(report.data?.phasesApproved ? "backoffice:statistics.methodology.phasesApproved" : "backoffice:statistics.methodology.phasesAwaiting");
  const years = useMemo(() => Array.from({ length: new Date().getFullYear() - 2015 + 1 }, (_, i) => 2015 + i), []);
  const setParam = (k: string, v: string) => setParams((p) => { p.set(k, v); return p; });
  const [exporting, setExporting] = useState(false);
  const exportXlsx = async () => {
    setExporting(true);
    try { await api.download(`/api/statistics/compstats.xlsx?from=${from}&to=${to}`); }
    catch (e) { notifySaveFailed(e, t("misc.error")); }
    finally { setExporting(false); }
  };

  return (
    <div>
      <PageHeader title={<span className="inline-flex items-center gap-2">{t("nav.statistics")}<InfoHint>{t("backoffice:statistics.sourcesHint")}</InfoHint></span>} className="mb-4" />
      <Tabs value={section} onValueChange={(v) => setParam("tab", v)}>
        <TabsList variant="underline" className="mb-4 flex-wrap">
          {SECTIONS.map((s) => <TabsTrigger key={s} value={s} variant="underline">{t(`backoffice:statistics.tabs.${s}`)}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="operations"><OperationsPanel /></TabsContent>
        {SECTIONS.filter((s) => s !== "operations").map((s) => (
          <TabsContent key={s} value={s} className="space-y-5">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-xl border border-sand-200 bg-white px-4 py-2.5">
              <Legend recordedFrom={report.data?.recordedFrom ?? null} canEdit={canEdit} />
              <div className="ms-auto flex items-center gap-2">
                <NativeSelect aria-label={t("backoffice:statistics.from")} className="h-8 w-[5.5rem] py-0 text-sm" value={from} onChange={(e) => setParam("from", e.target.value)}>{years.filter((y) => y <= to).map((y) => <option key={y} value={y}>{y}</option>)}</NativeSelect>
                <span className="text-muted-foreground" aria-hidden>–</span>
                <NativeSelect aria-label={t("backoffice:statistics.to")} className="h-8 w-[5.5rem] py-0 text-sm" value={to} onChange={(e) => setParam("to", e.target.value)}>{years.filter((y) => y >= from).map((y) => <option key={y} value={y}>{y}</option>)}</NativeSelect>
                <Button size="sm" variant="ghost" className="text-xs" disabled={from === QUESTIONNAIRE_PERIOD.from && to === QUESTIONNAIRE_PERIOD.to}
                  onClick={() => setParams((p) => { p.set("from", String(QUESTIONNAIRE_PERIOD.from)); p.set("to", String(QUESTIONNAIRE_PERIOD.to)); return p; })}>
                  {t("backoffice:statistics.questionnairePeriod")} {QUESTIONNAIRE_PERIOD.from}–{QUESTIONNAIRE_PERIOD.to}
                </Button>
                <Button size="sm" variant="gold" primary className="ms-1" loading={exporting} onClick={() => void exportXlsx()}><Download /> {t("backoffice:statistics.export")}</Button>
              </div>
            </div>
            <SignOffBar from={from} to={to} />
            {report.isLoading || !report.data ? <Skeleton className="h-80" /> : (
              <>
                <div className="grid items-start gap-5 xl:grid-cols-2">
                  {report.data.tables.filter((tb) => tb.section === s).map((tb) => <CompStatsTable key={tb.id} table={tb} canEdit={canEdit} signed={report.data!.signOffs ?? []} note={tb.id === "7.2" ? phasesNote : undefined} className={tb.columns.length + (tb.textColumn ? 2 : 0) > 3 ? "xl:col-span-2" : undefined} />)}
                </div>
                {(LISTS[s] ?? []).map((id) => <DecisionList key={id} id={id} rows={report.data!.decisions.filter((d) => d.table === id)} />)}
                {(ITEM_LISTS[s] ?? []).map((id) => <ItemList key={id} id={id} rows={report.data!.items.filter((d) => d.table === id)} />)}
                {s === "antitrust" || s === "mergers" ? <p className="text-xs leading-relaxed text-muted-foreground">{t(`backoffice:statistics.methodology.${s}`)}{s === "mergers" ? ` ${phasesNote}` : ""}</p> : null}
              </>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function Legend({ recordedFrom, canEdit }: { recordedFrom: number | null; canEdit: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
      {(["Computed", "Entered", "Derived"] as const).map((k) => <span key={k} className="inline-flex items-center gap-1.5"><span className={cn("size-2 rounded-full", SOURCE_STYLE[k])} />{t(`backoffice:statistics.legend.${k}`)}</span>)}
      <span className="inline-flex items-center gap-1.5"><span className="text-sand-400">—</span>{t("backoffice:statistics.legend.Missing")}</span>
      {recordedFrom ? <InfoHint>{t("backoffice:statistics.recordedFrom", { year: recordedFrom })}</InfoHint> : null}
      {canEdit ? <span className="hidden items-center gap-1 border-s border-sand-200 ps-4 2xl:inline-flex"><Pencil className="size-3" /> {t("backoffice:statistics.editHint")}</span> : null}
    </div>
  );
}

/**
 * The sign-off of each year's return shown, once the year has ended, by the authorised officer – the Director or above under the delegation-of-
 * authority matrix. A signed year's figures are those signed, with the data checks that had run, and take no entry until the year is reopened with reasons.
 */
function SignOffBar({ from, to }: { from: number; to: number }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const qc = useQueryClient();
  const staffName = useStaffName();
  const signOffs = useSignOffs(from, to);
  const [acting, setActing] = useState<{ year: number; reopen: boolean } | null>(null);
  const [note, setNote] = useState("");
  const act = useSave({
    mutationFn: () => api.post<{ year: number }>(`/api/statistics/returns/${acting!.year}/${acting!.reopen ? "reopen" : "signoff"}`, { note: note.trim() || null }),
    expect: "record",
    saved: (r) => t(acting?.reopen ? "backoffice:processes.statistics.reopened" : "backoffice:processes.statistics.signed", { year: r.year }),
    onSuccess: () => { setActing(null); setNote(""); void qc.invalidateQueries({ queryKey: ["compstats"] }); },
  });
  const d = signOffs.data;
  if (!d) return null;
  const years = Array.from({ length: Math.max(0, Math.min(to, d.lastSignable) - from + 1) }, (_, i) => from + i);
  if (years.length === 0) return null;
  const reopenValid = !acting?.reopen || note.trim().length >= 20;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-sand-200 bg-white px-4 py-2.5 text-xs" data-testid="statistics-signoffs">
      <span className="inline-flex items-center gap-1 font-medium text-aeblack-900">{t("backoffice:processes.statistics.signOffs")}<InfoHint>{t("backoffice:processes.statistics.signOffHint")}</InfoHint></span>
      {years.map((y) => {
        const signed = d.items.find((x) => x.year === y);
        return signed ? (
          <span key={y} className="inline-flex items-center gap-1">
            <Badge size="sm" variant="success" title={signed.note ?? undefined}><Lock /> {t("backoffice:processes.statistics.signedYear", { year: y, name: staffName(signed.signedByName), date: formatDate(signed.signedAt, locale) })}</Badge>
            {d.maySign ? <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setActing({ year: y, reopen: true })}><Unlock /> {t("backoffice:processes.statistics.reopen")}</Button> : null}
          </span>
        ) : (
          <span key={y} className="inline-flex items-center gap-1">
            <Badge size="sm" variant="outline">{t("backoffice:processes.statistics.unsignedYear", { year: y })}</Badge>
            {d.maySign ? <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setActing({ year: y, reopen: false })}><BadgeCheck /> {t("backoffice:processes.statistics.signOff")}</Button> : null}
          </span>
        );
      })}
      <Dialog open={acting !== null} onOpenChange={(o) => { if (!o) setActing(null); }}>
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>{t(acting?.reopen ? "backoffice:processes.statistics.reopenTitle" : "backoffice:processes.statistics.signTitle", { year: acting?.year })}</DialogTitle>
            <DialogDescription>{t(acting?.reopen ? "backoffice:processes.statistics.reopenHint" : "backoffice:processes.statistics.signHint")}</DialogDescription>
          </DialogHeader>
          <Field label={t(acting?.reopen ? "backoffice:processes.statistics.reasons" : "backoffice:processes.statistics.note")} required={acting?.reopen}>
            {(id) => <Textarea id={id} rows={3} value={note} onChange={(e) => setNote(e.target.value)} draft={acting ? `statistics:return:${acting.year}:${acting.reopen ? "reopen" : "signoff"}` : null} />}
          </Field>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setActing(null)}>{t("actions.cancel")}</Button>
            <Button variant="gold" disabled={!reopenValid} loading={act.isPending} onClick={() => act.mutate()}>{acting?.reopen ? <Unlock /> : <BadgeCheck />} {t(acting?.reopen ? "backoffice:processes.statistics.reopen" : "backoffice:processes.statistics.signOff")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CompStatsTable({ table, canEdit, signed, note, className }: { table: StatTable; canEdit: boolean; signed: SignOff[]; note?: string; className?: string }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const key = tableKey(table.id);
  return (
    <Card className={cn("min-w-0 overflow-hidden", className)}>
      <CardHeader className="space-y-0 px-5 py-3.5">
        <CardTitle className="flex items-center gap-2.5 text-[0.95rem] font-semibold">
          <span className="rounded-md bg-sand-100 px-1.5 py-0.5 font-mono text-[11px] font-medium text-sand-700">{questionOf(table.id)}</span>
          {t(`backoffice:statistics.tables.${key}.title`)}
          <InfoHint>{t(`backoffice:statistics.tables.${key}.hint`)}{note ? ` ${note}` : ""}</InfoHint>
        </CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto p-0 scrollbar-thin" tabIndex={0}>
        <table className="w-full min-w-max text-sm">
          <thead>
            <tr className="border-y border-sand-200 bg-sand-50/70 text-xs text-sand-700">
              <th className="w-20 px-5 py-2 text-start font-medium">{t("backoffice:statistics.year")}</th>
              {table.columns.map((c) => (
                <th key={c.key} className="px-4 py-2 text-end font-medium">
                  {t(`backoffice:statistics.columns.${c.key}`)}
                  {c.unit === "days" ? <span className="ms-1 normal-case tracking-normal text-sand-500">({t("backoffice:statistics.units.days")})</span> : null}
                </th>
              ))}
              {table.textColumn ? <th className="px-4 py-2 text-start font-medium">{t(`backoffice:statistics.texts.${table.textColumn}`)}</th> : null}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((r) => {
              // A signed year's figures are the ones signed, and take no entry until the return is reopened.
              const lock = signed.find((x) => x.year === r.year);
              const editable = canEdit && !lock;
              return (
                <tr key={r.year} className="border-b border-sand-100 last:border-0 even:bg-sand-50/30">
                  <td className="px-5 py-1.5 font-medium tabular text-aeblack-900">
                    <span className="inline-flex items-center gap-1">{r.year}{lock ? <Lock className="size-3 text-success-700" aria-label={t("backoffice:processes.statistics.lockedYear", { date: formatDate(lock.signedAt, locale) })} /> : null}</span>
                  </td>
                  {table.columns.map((c) => <td key={c.key} className="px-2 py-1 text-end"><ValueCell tableId={table.id} column={c} year={r.year} cell={r.cells[c.key]} canEdit={editable} /></td>)}
                  {table.textColumn ? <td className="px-2 py-1"><TextCell tableId={table.id} column={table.textColumn} year={r.year} text={r.text ?? null} source={r.textSource ?? null} canEdit={editable} /></td> : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function ValueCell({ tableId, column: col, year, cell, canEdit }: { tableId: string; column: StatColumn; year: number; cell: StatCell; canEdit: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const save = useSaveReturn();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  const [showFiles, setShowFiles] = useState(false);
  const column = col.key;
  // A computed figure lists the files it was calculated from (scenario 6.46).
  const traceable = col.computed && cell.computed != null && cell.computed > 0;
  const filesSheet = traceable ? (
    <FigureFilesSheet source={showFiles ? { metric: `${tableId}:${column}`, year } : null} title={`${questionOf(tableId)} · ${t(`backoffice:statistics.columns.${column}`)} · ${year}`} onClose={() => setShowFiles(false)} />
  ) : null;
  const shown = cell.value == null ? "—" : col.unit === "%" ? `${formatNumber(cell.value, locale)}${t("backoffice:statistics.units.%")}` : formatNumber(cell.value, locale);
  const tip = cell.source === "Entered"
    ? [t("backoffice:statistics.legend.Entered"), cell.enteredBy, cell.note, cell.computed != null ? t("backoffice:statistics.computedWas", { value: formatNumber(cell.computed, locale) }) : null].filter(Boolean).join(" · ")
    : t(`backoffice:statistics.legend.${cell.source}`);
  const content = (
    <span className={cn("inline-flex min-w-12 items-center justify-end gap-1.5 rounded-md px-2 py-1 tabular", cell.value == null ? "text-sand-400" : "text-aeblack-950")} title={tip}>
      {shown}
      <span className={cn("size-1.5 shrink-0 rounded-full", cell.source === "Missing" ? "bg-transparent" : SOURCE_STYLE[cell.source])} aria-hidden />
    </span>
  );
  if (!canEdit || col.derived) return traceable ? <><button type="button" onClick={() => setShowFiles(true)} className="rounded-md hover:bg-sand-100" aria-label={t("backoffice:r3Library.ops.filesOf", { value: shown })}>{content}</button>{filesSheet}</> : content;
  const submit = (clear: boolean) => save.mutate(
    { year, metric: `${tableId}:${column}`, value: clear ? null : value.trim() === "" ? null : Number(value.replace(/,/g, "")), note: clear ? null : note || null },
    { onSuccess: (answer) => { notifySaved(answer, t("backoffice:statistics.saved")); setOpen(false); } },
  );
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) { setValue(cell.source === "Entered" && cell.value != null ? String(cell.value) : ""); setNote(cell.source === "Entered" ? cell.note ?? "" : ""); } }}>
      <PopoverTrigger asChild><button type="button" className="rounded-md hover:bg-aegold-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-aegold-500">{content}</button></PopoverTrigger>
      <PopoverContent align="end" className="w-72 space-y-3">
        <p className="text-sm font-semibold">{tableId} · {t(`backoffice:statistics.columns.${column}`)} · {year}</p>
        {cell.computed != null ? <p className="text-xs text-muted-foreground">{t("backoffice:statistics.computedIs", { value: formatNumber(cell.computed, locale) })}</p> : null}
        <Field label={t("backoffice:statistics.value")}>{(id) => <Input id={id} inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} placeholder={cell.computed != null ? String(cell.computed) : ""} />}</Field>
        <Field label={t("backoffice:statistics.note")}>{(id) => <Textarea id={id} rows={2} value={note} onChange={(e) => setNote(e.target.value)} draft={`statistics:${tableId}:${column}:${year}:note`} />}</Field>
        {traceable ? <Button size="sm" variant="outline" className="w-full" onClick={() => { setOpen(false); setShowFiles(true); }}>{col.unit === "days" ? t("backoffice:r3Library.ops.showFiles") : t("backoffice:r3Library.ops.files", { count: cell.computed ?? 0 })}</Button> : null}
        <div className="flex justify-between gap-2">
          <Button size="sm" variant="ghost" disabled={cell.source !== "Entered"} loading={save.isPending} onClick={() => submit(true)}>{t("backoffice:statistics.clear")}</Button>
          <Button size="sm" variant="gold" disabled={value.trim() === "" || Number.isNaN(Number(value.replace(/,/g, "")))} loading={save.isPending} onClick={() => submit(false)}>{t("actions.save")}</Button>
        </div>
      </PopoverContent>
      {filesSheet}
    </Popover>
  );
}

function TextCell({ tableId, column, year, text, source, canEdit }: { tableId: string; column: string; year: number; text: string | null; source: string | null; canEdit: boolean }) {
  const { t } = useTranslation();
  const save = useSaveReturn();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const shown = <span className={cn("inline-block max-w-md truncate px-2 py-1 text-start text-xs", !text && "text-muted-foreground")} title={text ?? undefined}>{text || "—"}{source === "Entered" ? <span className="ms-1.5 inline-block size-1.5 rounded-full bg-aegold-600" /> : null}</span>;
  if (!canEdit) return shown;
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setValue(text ?? ""); }}>
      <PopoverTrigger asChild><button type="button" className="rounded-md text-start hover:bg-aegold-50">{shown}</button></PopoverTrigger>
      <PopoverContent align="start" className="w-80 space-y-3">
        <Field label={`${tableId} · ${t(`backoffice:statistics.texts.${column}`)} · ${year}`}>{(id) => <Textarea id={id} rows={3} value={value} onChange={(e) => setValue(e.target.value)} draft={`statistics:${tableId}:${column}:${year}:text`} />}</Field>
        <div className="flex justify-between gap-2">
          <Button size="sm" variant="ghost" disabled={source !== "Entered"} loading={save.isPending} onClick={() => save.mutate({ year, metric: `${tableId}:${column}`, text: null }, { onSuccess: () => setOpen(false) })}>{t("backoffice:statistics.clear")}</Button>
          <Button size="sm" variant="gold" disabled={!value.trim()} loading={save.isPending} onClick={() => save.mutate({ year, metric: `${tableId}:${column}`, text: value }, { onSuccess: (answer) => { notifySaved(answer, t("backoffice:statistics.saved")); setOpen(false); } })}>{t("actions.save")}</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function DecisionList({ id, rows }: { id: string; rows: DecisionListRow[] }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const key = tableKey(id);
  const merger = id === "7.6";
  const columns = merger
    ? (["number", "year", "parties", "outcome", "remedies"] as const)
    : (["number", "year", "conduct", "companies", id === "10.8" ? "instrument" : "sanction", "fine"] as const);
  return (
    <Card className="min-w-0">
      <ListHeader id={id} title={t(`backoffice:statistics.lists.${key}`)} />
      <CardContent className="overflow-x-auto p-0 scrollbar-thin" tabIndex={0}>
        {rows.length === 0 ? <p className="border-t border-sand-100 px-5 py-4 text-sm text-muted-foreground">{t("backoffice:statistics.noDecisions")}</p> : (
          <table className="w-full min-w-max text-sm">
            <thead>
              <tr className="border-y border-sand-200 bg-sand-50/60 text-xs text-sand-700">
                {columns.map((c) => <th key={c} className={cn("px-4 py-2 font-medium", c === "fine" || c === "year" ? "text-end" : "text-start")}>{t(`backoffice:statistics.decisionColumns.${c}`)}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={`${d.caseId}-${d.decisionNumber}`} className="cursor-pointer border-b border-sand-100 last:border-0 hover:bg-sand-50" onClick={() => navigate(`/cases/${d.caseId}?tab=decision`)}>
                  <td className="px-4 py-2 font-mono text-xs">{d.decisionNumber ?? d.caseNumber}</td>
                  <td className="px-4 py-2 text-end tabular">{d.year}</td>
                  {merger ? (
                    <>
                      <td className="max-w-80 truncate px-4 py-2" title={d.companies}>{d.companies}</td>
                      <td className="px-4 py-2">{t(`backoffice:statistics.sanctions.${d.sanction}`, { defaultValue: d.sanction })}</td>
                      <td className="max-w-md truncate px-4 py-2 text-xs text-muted-foreground" title={d.detail ?? undefined}>{d.detail ?? "—"}</td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-2">{t(`conducts.${d.conduct}`, { defaultValue: d.conduct })}</td>
                      <td className="max-w-80 truncate px-4 py-2" title={d.companies}>{d.companies}</td>
                      <td className="px-4 py-2">{t(`backoffice:statistics.sanctions.${d.sanction}`, { defaultValue: d.sanction })}</td>
                      <td className="px-4 py-2 text-end tabular">{d.fineAed != null ? formatNumber(d.fineAed, locale) : "—"}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

function ItemList({ id, rows }: { id: string; rows: ItemListRow[] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const columns = ["year", "title", "sector", id === "13.2" ? "duration" : "addressee"] as const;
  return (
    <Card className="min-w-0">
      <ListHeader id={id} title={t(`backoffice:statistics.lists.${tableKey(id)}`)} />
      <CardContent className="overflow-x-auto p-0 scrollbar-thin" tabIndex={0}>
        {rows.length === 0 ? <p className="border-t border-sand-100 px-5 py-4 text-sm text-muted-foreground">{t("backoffice:statistics.noItems")}</p> : (
          <table className="w-full min-w-max text-sm">
            <thead>
              <tr className="border-y border-sand-200 bg-sand-50/60 text-xs text-sand-700">
                {columns.map((c) => <th key={c} className={cn("px-4 py-2 font-medium", c === "year" ? "w-20 text-end" : "text-start")}>{t(`backoffice:statistics.itemColumns.${c}`)}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.link ?? r.title}-${i}`} className={cn("border-b border-sand-100 last:border-0", r.link && "cursor-pointer hover:bg-sand-50")} onClick={r.link ? () => navigate(r.link!) : undefined}>
                  <td className="px-4 py-2 text-end tabular">{r.year}</td>
                  <td className="max-w-lg truncate px-4 py-2 font-medium text-aeblack-900" title={r.title}>{r.title}</td>
                  <td className="px-4 py-2">{r.sector ?? "—"}</td>
                  <td className="max-w-72 truncate px-4 py-2 text-muted-foreground" title={r.detail ?? undefined}>
                    {id === "13.2" && r.detail ? r.detail.replace(/ days$/, ` ${t("backoffice:statistics.units.days")}`) : r.detail ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

function ListHeader({ id, title }: { id: string; title: string }) {
  return (
    <CardHeader className="space-y-0 px-5 py-3.5">
      <CardTitle className="flex items-center gap-2.5 text-[0.95rem] font-semibold"><span className="rounded-md bg-sand-100 px-1.5 py-0.5 font-mono text-[11px] font-medium text-sand-700">{id}</span>{title}</CardTitle>
    </CardHeader>
  );
}
